import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  StoreHeader,
  HeroBanner,
  TrustBadges,
  CategoryGrid,
  ProductGrid,
  CartSheet,
  StoreFooter,
  CollectionCollage,
} from "@/components/store";
import { useCart } from "../context/CartContext";
import { useStore } from "../hooks/useStore";
import { getImageUrl } from "../utils/imageUtils";
import { storePath, getSubdomain } from "../utils/storeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

// Default theme matching Infinity Urns style
const DEFAULT_THEME = {
  primary_color: "#1a365d",
  secondary_color: "#2c5282",
  accent_color: "#d69e2e",
  background_color: "#ffffff",
  text_color: "#1a202c",
  font_family: "Cormorant Garamond, serif"
};

export default function StorePreview() {
  const { subdomain: paramSubdomain } = useParams();
  // When accessed via cenosid.edisplayroom.com, useParams returns undefined.
  // Fall back to hostname detection so useStore() always gets a valid subdomain.
  const subdomain = paramSubdomain || getSubdomain();
  const navigate = useNavigate();

  // Use custom hook for all store data
  const {
    store,
    theme,
    storeAppearance,
    platformInfo,
    shippingRates,
    categories,
    collections,
    loading
  } = useStore(subdomain);

  // Fetch homepage products from paginated API
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    if (!subdomain || !store) return;
    const fetchHomepageProducts = async () => {
      try {
        setProductsLoading(true);
        const res = await publicAxios.get(`${API}/public/store/${subdomain}/products?limit=12&page=1`);
        setProducts(res.data.products || []);
        setTotalProducts(res.data.total || 0);
      } catch (err) {
        console.error('Failed to fetch homepage products:', err);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchHomepageProducts();
  }, [subdomain, store]);

  // Cart state from context
  const {
    cart,
    cartOpen,
    setCartOpen,
    addToCart: baseAddToCart,
    removeFromCart,
    updateQuantity,
    cartCount,
    cartSubtotal
  } = useCart();

  const addToCart = (product) => baseAddToCart({ ...product, store_subdomain: subdomain });

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  // Cart functions now come from context

  // Cart calculations moved to context or simplified

  const shippingCost = useMemo(() => {
    if (!shippingRates.shipping_enabled) return 0;
    if (cartSubtotal >= shippingRates.free_shipping_threshold) return 0;

    const sortedTiers = [...(shippingRates.shipping_tiers || [])].sort((a, b) => a.min_value - b.min_value);

    for (const tier of sortedTiers) {
      if (cartSubtotal >= tier.min_value && cartSubtotal < tier.max_value) {
        return tier.shipping_cost;
      }
    }

    return sortedTiers.length > 0 ? sortedTiers[sortedTiers.length - 1].shipping_cost : 10;
  }, [cartSubtotal, shippingRates]);

  const cartTotal = cartSubtotal + shippingCost;

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setCartOpen(false);
    navigate(storePath(subdomain, "/checkout"));
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = products;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [products, searchTerm]);

  // Loading state with Skeleton UI
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {/* Skeleton Header */}
        <div className="h-16 border-b flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg animate-shimmer" />
            <div className="w-32 h-6 rounded animate-shimmer" />
          </div>
          <div className="flex gap-4">
            <div className="w-20 h-4 rounded animate-shimmer" />
            <div className="w-20 h-4 rounded animate-shimmer" />
          </div>
        </div>

        {/* Skeleton Hero */}
        <div className="w-full h-[400px] animate-shimmer mb-12" />

        {/* Skeleton Collections */}
        <div className="max-w-7xl mx-auto px-4 mb-12">
          <div className="w-48 h-8 rounded animate-shimmer mx-auto mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 rounded-2xl animate-shimmer" />
            ))}
          </div>
        </div>

        {/* Skeleton Products */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="w-64 h-8 rounded animate-shimmer mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="space-y-3">
                <div className="aspect-square rounded-xl animate-shimmer" />
                <div className="w-3/4 h-4 rounded animate-shimmer" />
                <div className="w-1/2 h-4 rounded animate-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Store not found
  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Store Not Found</h1>
          <p className="text-slate-600 mb-4">The store you are looking for does not exist.</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">

      {/* Hero Banner */}
      <HeroBanner theme={theme} storeAppearance={storeAppearance} />

      {/* Trust Badges */}
      <TrustBadges theme={theme} storeAppearance={storeAppearance} />

      {/* Featured Collections */}
      {collections.length > 0 && (
        <section className="py-12 px-4 lg:px-8 border-b border-slate-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: theme.primary_color }}>
              Featured Collections
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {collections.filter(c => c.is_featured).sort((a, b) => {
                const orderA = a.featured_order || 999;
                const orderB = b.featured_order || 999;
                return orderA - orderB;
              }).map((coll) => {
                const sampleImages = coll.sample_images?.map(img => getImageUrl(img)) || [];

                return (
                  <div key={coll.collection_id || coll._id} className="group cursor-pointer rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 bg-white">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <CollectionCollage
                        images={coll.cover_image ? [] : sampleImages}
                        name={coll.name}
                        fallbackImage={coll.cover_image ? getImageUrl(coll.cover_image) : "https://images.unsplash.com/photo-1544605562-f72fca6c598d?auto=format&fit=crop&q=80&w=800"}
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors pointer-events-none" />
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-xl font-bold" style={{ color: theme.primary_color }}>{coll.name}</h3>
                        <span className="text-xs font-medium px-2 py-1 bg-slate-100 rounded-md text-slate-500">

                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{coll.description}</p>
                      <Button
                        variant="outline"
                        className="w-full rounded-full border-slate-200 hover:bg-slate-50"
                        onClick={() => navigate(storePath(subdomain, `/shop?collection_id=${coll.collection_id || coll._id}`))}
                      >
                        Browse Collection
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}


      <ProductGrid
        products={filteredProducts.slice(0, 12)}
        collections={collections}
        theme={theme}
        viewMode={viewMode}
        setViewMode={setViewMode}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        onAddToCart={addToCart}
        subdomain={subdomain}
        totalCount={filteredProducts.length}
      />

      {totalProducts > 12 && (
        <div className="flex justify-center pb-20 bg-slate-50">
          <Button
            size="lg"
            className="rounded-full px-12 font-bold shadow-lg hover:shadow-xl transition-all"
            style={{ backgroundColor: theme.primary_color }}
            onClick={() => navigate(storePath(subdomain, "/shop"))}
          >
            View All Products
          </Button>
        </div>
      )}
    </div>
  );
}
